"use client";

import dynamic from "next/dynamic";
import { useEffect, useState, useRef } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import type { FeaturedGalleryItem } from "@/lib/gallery-types";

/** The seed shape the home page hands down. Declared once, in
 *  `lib/gallery-types.ts`, so this wrapper and `<FeaturedGallery>` cannot drift
 *  from each other or from the page that seeds them. */
type GalleryItem = FeaturedGalleryItem;

// SSR is intentionally ON now. The home page already fetches the gallery
// items server-side and passes them through `initialItems`, so the section
// structure + image alt text render into the static HTML (crawlers + a11y)
// instead of being client-only. The component is still code-split (dynamic)
// and below-the-fold work is deferred by the IntersectionObserver gate below.
const FeaturedGallery = dynamic(
  () => import("./FeaturedGallery").then((m) => ({ default: m.FeaturedGallery })),
  {
    loading: () => (
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="aspect-square w-full rounded-2xl" />
        ))}
      </div>
    ),
  }
);

/** Renders FeaturedGallery only when the section scrolls into view to defer JS and fetch. */
export function LazyFeaturedGallery({ initialItems }: { initialItems?: GalleryItem[] } = {}) {
  const ref = useRef<HTMLDivElement>(null);
  // When the page seeded us with gallery items there's no fetch to defer, so
  // render immediately (incl. on the server) — that puts the alt text in the
  // SSR HTML. Without seeded data we keep the IntersectionObserver gate so the
  // client-side fetch is deferred until the section nears the viewport.
  const hasInitial = Boolean(initialItems && initialItems.length > 0);
  const [inView, setInView] = useState(hasInitial);

  useEffect(() => {
    if (hasInitial) return; // already rendering — no observer needed
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => entry?.isIntersecting && setInView(true),
      { rootMargin: "100px", threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasInitial]);

  return (
    <div ref={ref}>
      {inView ? (
        <FeaturedGallery initialItems={initialItems} />
      ) : (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="aspect-square w-full rounded-2xl" />
          ))}
        </div>
      )}
    </div>
  );
}
